import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { VendorSpend } from '@/hooks/useSpendAnalytics';
import { Loader2 } from 'lucide-react';

interface SpendByVendorChartProps {
  data: VendorSpend[] | undefined;
  isLoading: boolean;
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--muted-foreground))', // For "All Others"
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function truncateName(name: string, maxLength: number = 20): string {
  return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
}

export function SpendByVendorChart({ data, isLoading }: SpendByVendorChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Vendors by Spend</CardTitle>
          <CardDescription>Top 10 vendors by total spend in period</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Vendors by Spend</CardTitle>
          <CardDescription>Top 10 vendors by total spend in period</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">No vendor spend data for this period</p>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data with truncated names for display
  const chartData = data.map(vendor => ({
    ...vendor,
    displayName: truncateName(vendor.vendorName),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Vendors by Spend</CardTitle>
        <CardDescription>Top 10 vendors by total spend in period</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              type="number"
              tickFormatter={(value) => formatCurrency(value)}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            />
            <YAxis
              type="category"
              dataKey="displayName"
              width={140}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number, name: string, props: any) => [
                `${formatCurrency(value)}`,
                'Spend',
              ]}
              labelFormatter={(label, payload) => {
                const vendor = payload?.[0]?.payload;
                return vendor ? `${vendor.vendorName} (${vendor.jobCount} jobs)` : label;
              }}
            />
            <Bar dataKey="spend" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.isOther ? 'hsl(var(--muted-foreground))' : COLORS[index % 10]} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
